/** @jsxImportSource theme-ui */
import { Formik } from 'formik';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Input } from 'semantic-ui-react';
import * as Yup from 'yup';

import { sxtheme } from 'utils/cssHelpers';
import { nameof } from 'utils/typeHelpers';
import { toAbsoluteAuthUrl } from 'utils/urlHelpers';
import { PhoneRegExp } from 'utils/validationPatterns';

import { BdIcon } from 'components/_common/BdIcon';
import { FlexGroup } from 'components/_common/FlexGroup';
import { Typography } from 'components/_common/Typography';

import { updateUserProfile } from 'user/user.saga';
import * as UserSelectors from 'user/user.selectors';

import BdFormLabel from '../_common/BdFormLabel';
import SettingsSubmitButton from '../_common/SettingsSubmitButton';
import {
  FlexFormRow,
  FormFieldGroup,
  InputErrorMessage,
  PageHeading,
} from '../CustomSettings/CustomSettingsForm';

type AccountDetailsFM = {
  firstName: string;
  lastName: string;
  roleId: string;
  organization: string;
  locationGeonameId: number;
  username: string;
  phoneNumber: string;
  isExternal: boolean;
};

const FieldLabel: React.FC = ({ children }) => (
  <Typography sx={{ pt: '10px', pb: '10px' }} variant="body2" color="stone90">
    {children}
  </Typography>
);
const FieldValue: React.FC = ({ children }) => (
  <Typography variant="body1" color="stone90">
    {children}
  </Typography>
);

const AccountDetails: React.FC = () => {
  const dispatch = useDispatch();
  const userProfile = useSelector(UserSelectors.userProfileSelector);
  const isSubmitting = useSelector(UserSelectors.isSettingsLoadingSelector);

  const userDetails = userProfile && userProfile.personalDetails;
  const userLocation = userProfile && userProfile.location;
  const seedValue: AccountDetailsFM = {
    firstName: userDetails?.firstName ?? '',
    lastName: userDetails?.lastName ?? '',
    roleId: userProfile?.userType.id,
    organization: userDetails?.organization ?? '',
    locationGeonameId: userLocation?.geonameId,
    username: userProfile?.userName ?? '',
    phoneNumber: userDetails?.phoneNumber ?? '',
    isExternal: userProfile?.isExternal,
  };
  const formValidationSchema = Yup.object().shape({
    firstName: Yup.string()
      .required('Your first name is required')
      .max(256, 'Your first name cannot be longer than 256 characters'),
    lastName: Yup.string()
      .required('Your last name is required')
      .max(256, 'Your last name cannot be longer than 256 characters'),
    organization: Yup.string().max(400, 'Organization cannot be longer than 400 characters'),
    phoneNumber: Yup.string().matches(PhoneRegExp, 'Phone number is not valid'),
  });
  const ExternalUser: React.FC<AccountDetailsFM> = ({ ...props }) => (
    <div>
      <FlexGroup
        sx={{
          alignItems: 'center',
          mb: '16px',
          background: sxtheme(t => t.colors.deepSea20),
          display: ['none', 'flex'],
        }}
        prefix={<BdIcon sx={{ pl: '16px' }} name="icon-key-lock" color="deepSea90" bold />}
      >
        <Typography sx={{ p: '16px 16px 16px 32px' }} variant="body1">
          Your account is managed by your organization’s Single Sign-on (SSO) provider. Please
          contact your plan administrator for more details.
        </Typography>
      </FlexGroup>

      <div>
        <PageHeading>My Profile</PageHeading>
      </div>
      <div>
        <div sx={{ display: 'flex', flexDirection: 'column' }}>
          <FlexFormRow>
            <FormFieldGroup>
              <FieldLabel>First Name</FieldLabel>
              <FieldValue>{props.firstName}</FieldValue>
            </FormFieldGroup>
            <FormFieldGroup sx={{ mt: ['2em', '0'] }}>
              <FieldLabel>Last Name</FieldLabel>
              <FieldValue>{props.lastName}</FieldValue>
            </FormFieldGroup>
          </FlexFormRow>
        </div>
        <div sx={{ display: 'flex', flexDirection: 'column', mt: [null, '1.5em'] }}>
          <FlexFormRow>
            <FormFieldGroup sx={{ mt: ['2em', '0'] }}>
              <FieldLabel>Organization (optional)</FieldLabel>
              <FieldValue>{props.organization}</FieldValue>
            </FormFieldGroup>
            <FormFieldGroup sx={{ mt: ['2em', '0'] }}>
              <FieldLabel>Phone Number (optional)</FieldLabel>
              <FieldValue>{props.phoneNumber}</FieldValue>
            </FormFieldGroup>
          </FlexFormRow>
        </div>
      </div>
      <div sx={{ pt: '48px' }}>
        <PageHeading>My BlueDot Credentials</PageHeading>
      </div>
      <div sx={{ display: 'flex', flexDirection: 'column' }}>
        <FlexFormRow>
          <FormFieldGroup>
            <FieldLabel>Username</FieldLabel>
            <FieldValue>{props.username}</FieldValue>
          </FormFieldGroup>
          <FormFieldGroup sx={{ mt: ['2em', '0'] }}>
            <FieldLabel>Password</FieldLabel>
            <FieldValue>Managed through single sign-on (SSO)</FieldValue>
          </FormFieldGroup>
        </FlexFormRow>
      </div>
    </div>
  );

  const handleFormSubmit = (values: AccountDetailsFM) => {
    dispatch(
      updateUserProfile({
        ...seedValue,
        firstName: values.firstName,
        lastName: values.lastName,
        organization: values.organization || undefined,
        phoneNumber: values.phoneNumber || undefined,
      })
    );
  };

  return (
    <div sx={{ alignSelf: 'stretch' }}>
      {seedValue.isExternal ? (
        <ExternalUser {...seedValue} />
      ) : (
        <div>
          <PageHeading>My Profile</PageHeading>
          <Formik<AccountDetailsFM>
            enableReinitialize
            initialValues={seedValue}
            validationSchema={formValidationSchema}
            onSubmit={values => handleFormSubmit(values)}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              dirty,
              isValid,
            }) => {
              return (
                <form onSubmit={handleSubmit} className={dirty ? 'was-validated' : ''}>
                  <div sx={{ display: 'flex', flexDirection: 'column' }}>
                    <FlexFormRow>
                      <FormFieldGroup>
                        <BdFormLabel>First Name</BdFormLabel>
                        <Input
                          fluid
                          name={nameof<AccountDetailsFM>('firstName')}
                          error={touched.firstName && !!errors.firstName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.firstName}
                          className={`form-control ${
                            touched.firstName && errors.firstName ? 'is-invalid' : ''
                          }`}
                        />
                        <InputErrorMessage>{errors.firstName}</InputErrorMessage>
                      </FormFieldGroup>
                      <FormFieldGroup>
                        <BdFormLabel>Last Name</BdFormLabel>
                        <Input
                          fluid
                          name={nameof<AccountDetailsFM>('lastName')}
                          error={touched.lastName && !!errors.lastName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.lastName}
                          className={`form-control ${
                            touched.lastName && errors.lastName ? 'is-invalid' : ''
                          }`}
                        />
                        <InputErrorMessage>{errors.lastName}</InputErrorMessage>
                      </FormFieldGroup>
                    </FlexFormRow>
                    <FlexFormRow>
                      <FormFieldGroup>
                        <BdFormLabel>Organization (optional)</BdFormLabel>
                        <Input
                          fluid
                          placeholder="None"
                          name={nameof<AccountDetailsFM>('organization')}
                          error={touched.organization && !!errors.organization}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.organization}
                        />
                        <InputErrorMessage />
                      </FormFieldGroup>
                      <FormFieldGroup>
                        <BdFormLabel>Phone Number (optional)</BdFormLabel>
                        <Input
                          fluid
                          placeholder="None"
                          name={nameof<AccountDetailsFM>('phoneNumber')}
                          error={touched.phoneNumber && !!errors.phoneNumber}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.phoneNumber}
                        />
                        <InputErrorMessage>{errors.phoneNumber}</InputErrorMessage>
                      </FormFieldGroup>
                    </FlexFormRow>
                  </div>
                  <SettingsSubmitButton
                    disabled={!isValid || !dirty || isSubmitting}
                    text="Save Changes"
                  />
                </form>
              );
            }}
          </Formik>
          <div sx={{ pt: '48px' }}>
            <PageHeading>My BlueDot Credentials</PageHeading>
          </div>
          <div sx={{ display: 'flex', flexDirection: 'column' }}>
            <FlexFormRow>
              <FormFieldGroup>
                <FieldLabel>Username</FieldLabel>
                <FieldValue>{seedValue.username}</FieldValue>
              </FormFieldGroup>
              <div sx={{ display: 'flex', flex: '1 0 320px' }}>
                <FormFieldGroup sx={{ flex: 1 }}>
                  <FieldLabel>Password</FieldLabel>
                  <FieldValue>••••••••••••</FieldValue>
                </FormFieldGroup>
                <FormFieldGroup sx={{ flex: 1, textAlign: 'end' }}>
                  <div sx={{ pt: '30px' }}>
                    <a
                      sx={{ display: 'flex', justifyContent: 'flex-end' }}
                      href={toAbsoluteAuthUrl('/Account/ChangePassword')}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Typography
                        variant="body2"
                        color="sea90"
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            color: sxtheme(t => t.colors.sea60),
                            textDecoration: 'underline',
                            transition: 'ease .3s',
                          },
                        }}
                      >
                        Change Password
                      </Typography>
                      <BdIcon name="icon-direct-link" color="sea90" bold />
                    </a>
                  </div>
                </FormFieldGroup>
              </div>
            </FlexFormRow>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountDetails;
